import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLogin } from '../hooks/useLogin';
import { LoginError } from '../api';

const OAUTH_ERROR_KEYS: Record<string, string> = {
  missing_code: 'auth.googleSignInCancelled',
  no_account: 'auth.googleNoAccount',
  account_disabled: 'auth.googleAccountDisabled',
  auth_error: 'auth.googleAuthError',
};

// ATEK Brand Colors
const BRAND = {
  cobalt: '#5A1DEF',
  slate: '#404041',
} as const;

// reCAPTCHA Enterprise - only enabled if VITE_RECAPTCHA_KEY is set
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_KEY as string | undefined;

// Type for grecaptcha Enterprise API
declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

// Dynamically load reCAPTCHA script if not already loaded
let recaptchaScriptLoaded = false;
function loadRecaptchaScript(): Promise<void> {
  if (!RECAPTCHA_SITE_KEY || recaptchaScriptLoaded) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      recaptchaScriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      console.warn('Failed to load reCAPTCHA script');
      resolve();
    };
    document.head.appendChild(script);
  });
}

async function getRecaptchaToken(): Promise<string | undefined> {
  // Skip reCAPTCHA if not configured
  if (!RECAPTCHA_SITE_KEY) {
    return undefined;
  }

  // Ensure script is loaded
  await loadRecaptchaScript();

  return new Promise((resolve) => {
    if (!window.grecaptcha?.enterprise) {
      console.warn('reCAPTCHA not loaded');
      resolve(undefined);
      return;
    }
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha!.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'LOGIN' });
        resolve(token);
      } catch (error) {
        console.error('reCAPTCHA error:', error);
        resolve(undefined);
      }
    });
  });
}

interface LoginFormProps {
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const loginMutation = useLogin();

  const oauthError = searchParams.get('error');
  const oauthStage = searchParams.get('stage');
  const oauthErrorMessage = oauthError
    ? `${OAUTH_ERROR_KEYS[oauthError] ? t(OAUTH_ERROR_KEYS[oauthError]) : t('auth.googleGenericError', { code: oauthError })}${oauthStage ? ` ${t('auth.googleStage', { stage: oauthStage })}` : ''}`
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Get reCAPTCHA token before login
    const recaptchaToken = await getRecaptchaToken();

    loginMutation.mutate(
      {
        username: email,
        password,
        rememberMe,
        recaptchaToken,
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (error) => {
          if (error.errorCode === 'force_password_reset' && error.resetToken) {
            navigate(`/password-reset/${encodeURIComponent(error.resetToken)}`);
          }
        },
      }
    );
  };

  const getErrorMessage = () => {
    if (!loginMutation.error) return null;

    const error = loginMutation.error as LoginError;

    // Transient throttle from the backend's per-attempt backoff — not a hard lock.
    if (error.status === 429 && error.info?.account_locked === false) {
      return t('auth.tooManyAttempts');
    }

    // Account hard-locked (enabled=false) — requires admin unlock.
    if (error.status === 429 || error.info?.account_locked) {
      return t('auth.accountLocked');
    }

    // Distinct 403 paths — surface specific reasons before the generic "wrong credentials".
    if (error.errorCode === 'account_disabled') {
      return t('auth.accountDisabled');
    }
    if (error.errorCode === 'employment_type_not_allowed') {
      return t('auth.employmentTypeNotAllowed');
    }
    if (error.errorCode === 'force_password_reset') {
      return t('auth.forcePasswordResetRequired');
    }
    if (error.errorCode === 'failed_recaptcha' || error.errorCode === 'invalid_recaptcha_token' || error.errorCode === 'missing_recaptcha_token') {
      return t('auth.recaptchaFailed');
    }

    // Wrong credentials with attempts warning
    if (error.status === 401 || error.status === 403) {
      const attempts = error.info?.incorrect_login_attempts;
      if (typeof attempts === 'number' && attempts >= 1) {
        const remaining = 3 - attempts;
        if (remaining > 0) {
          return `${t('auth.wrongCredentials')} ${t('auth.attemptsRemaining', { count: remaining })}`;
        }
      }
      return t('auth.wrongCredentials');
    }

    // Missing credentials
    if (error.status === 400) {
      return t('auth.missingCredentials');
    }

    // Generic error
    return error.message || t('error');
  };

  const errorMessage = getErrorMessage();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {oauthErrorMessage && (
        <Alert variant="destructive">
          <AlertDescription>{oauthErrorMessage}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">{t('auth.email')}</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          autoFocus
          disabled={loginMutation.isPending}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">{t('auth.password')}</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          disabled={loginMutation.isPending}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="rememberMe"
          checked={rememberMe}
          onCheckedChange={setRememberMe}
          disabled={loginMutation.isPending}
        />
        <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
          {t('auth.rememberMe')}
        </Label>
      </div>

      <Button
        type="submit"
        className="w-full text-white font-medium"
        style={{ backgroundColor: BRAND.cobalt }}
        disabled={loginMutation.isPending}
      >
        {loginMutation.isPending ? t('auth.signingIn') : t('auth.signIn')}
      </Button>

      <div className="text-center">
        <a
          href="/password-recovery"
          className="text-sm hover:underline"
          style={{ color: BRAND.cobalt }}
        >
          {t('auth.forgotPassword')}
        </a>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-muted-foreground">{t('auth.or')}</span>
        </div>
      </div>

      <a
        href="/auth/google"
        className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
          <g fill="none" fillRule="evenodd">
            <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </g>
        </svg>
        {t('auth.signInWithGoogle')}
      </a>

      <p className="text-center text-xs" style={{ color: '#9ca3af' }}>
        {t('auth.internalStaffOnly')}
      </p>
    </form>
  );
}
